import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { api, errMsg } from "@/lib/api";
import { PERMISSIONS } from "@/lib/format";
import { PageHeader } from "@/components/kit";

const PermGrid = ({ value, onChange, disabled, idp }) => (
  <div className="grid grid-cols-2 gap-2 text-sm">
    {Object.entries(PERMISSIONS).map(([k, l]) => (
      <label key={k} className="flex items-center gap-2 cursor-pointer">
        <Checkbox disabled={disabled} checked={value.includes(k)} onCheckedChange={(v) => onChange(v ? [...value, k] : value.filter((x) => x !== k))} data-testid={`${idp}-perm-${k}`} />{l}
      </label>))}
  </div>
);

const AdminCard = ({ a, onDone }) => {
  const [perms, setPerms] = useState(a.permissions || []);
  const save = async (extra = {}) => { try { await api.patch(`/admin/admins/${a.id}`, { permissions: perms, ...extra }); toast.success("Administrador atualizado"); onDone(); } catch (e) { toast.error(errMsg(e)); } };
  return (
    <div className="surface p-5 md:p-6" data-testid={`admin-card-${a.id}`}>
      <div className="flex justify-between gap-2"><div><div className="font-semibold">{a.name}</div><div className="text-xs text-muted-foreground">{a.email}</div></div>
        {a.is_super ? <span className="text-xs font-bold text-primary">SUPER ADMIN</span> : <span className={`text-xs font-bold ${a.status === "active" ? "up" : "down"}`}>{a.status === "active" ? "ATIVO" : "SUSPENSO"}</span>}</div>
      <div className="mt-4"><PermGrid value={a.is_super ? Object.keys(PERMISSIONS) : perms} onChange={setPerms} disabled={a.is_super} idp={`admin-${a.id}`} /></div>
      {!a.is_super && <div className="mt-4 flex gap-2">
        <Button size="sm" onClick={() => save()} data-testid={`admin-save-${a.id}`}>Salvar permissões</Button>
        <Button size="sm" variant="outline" onClick={() => save({ status: a.status === "active" ? "suspended" : "active" })} data-testid={`admin-toggle-${a.id}`}>{a.status === "active" ? "Suspender" : "Reativar"}</Button>
      </div>}
    </div>
  );
};

export default function AdminAdmins() {
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ name: "", email: "", password: "", permissions: ["clients"] });
  const load = () => api.get("/admin/admins").then((r) => setRows(r.data));
  useEffect(() => { load().catch(() => {}); }, []);
  const create = async (e) => {
    e.preventDefault();
    try { await api.post("/admin/admins", f); toast.success("Administrador criado"); setOpen(false); setF({ name: "", email: "", password: "", permissions: ["clients"] }); load(); }
    catch (err) { toast.error(errMsg(err)); }
  };
  return (
    <div data-testid="admin-admins-page">
      <PageHeader eyebrow="Acesso" title="Administradores e permissões" desc="Conceda a cada administrador apenas as permissões necessárias."
        actions={<Button className="h-11" onClick={() => setOpen(true)} data-testid="open-create-admin-btn"><Plus className="mr-2 h-4 w-4" />Novo administrador</Button>} />
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">{rows.map((a) => <AdminCard key={a.id} a={a} onDone={load} />)}</div>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="create-admin-dialog">
          <DialogHeader><DialogTitle>Novo administrador</DialogTitle></DialogHeader>
          <form onSubmit={create} className="space-y-3">
            {[["name", "Nome"], ["email", "E-mail"], ["password", "Senha inicial"]].map(([k, l]) => (
              <div key={k} className="space-y-1.5"><Label>{l}</Label><Input required className="h-11" type={k === "email" ? "email" : "text"} value={f[k]} onChange={(e) => setF({ ...f, [k]: e.target.value })} data-testid={`new-admin-${k}-input`} /></div>))}
            <PermGrid value={f.permissions} onChange={(p) => setF({ ...f, permissions: p })} idp="new-admin" />
            <DialogFooter><Button className="h-11" data-testid="new-admin-submit">Criar administrador</Button></DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
